// ================================================================
// 💰 SALES REPORT - Relatório de Vendas & Comissões
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors'

// Registrar componentes do ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const SalesReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesData, setSalesData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartColors  = useChartsColors();
  /**
   * Handle mudança de filtros de usuário
   */
  const handleUserFilterChange = (userFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters: {
          ...prev.advancedFilters,
          user_type: userFilters.user_type || undefined,
          user_id: userFilters.user_id || undefined
        }
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadSalesReportWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Carrega relatório com filtros específicos (usado quando filtros mudam)
   */
  const loadSalesReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('💰 Carregando Relatório de Vendas com filtros...', filtersToUse);

      const result = await ReportsService.getSalesReport({
        period: filtersToUse.period,
        user_type: filtersToUse.advancedFilters?.user_type,
        user_id: filtersToUse.advancedFilters?.user_id,
        product_id: filtersToUse.advancedFilters?.product_id,
        payment_method: filtersToUse.advancedFilters?.payment_method,
        sale_status: filtersToUse.advancedFilters?.sale_status,
        region: filtersToUse.advancedFilters?.region
      });

      if (result.success) {
        setSalesData(result.data);
        console.log('✅ Relatório de Vendas carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Carrega dados do relatório
   */
  const loadSalesReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('💰 Carregando Relatório de Vendas...', filters);

      const result = await ReportsService.getSalesReport({
        period: filters.period,
        user_type: filters.advancedFilters?.user_type,
        user_id: filters.advancedFilters?.user_id,
        product_id: filters.advancedFilters?.product_id,
        payment_method: filters.advancedFilters?.payment_method,
        sale_status: filters.advancedFilters?.sale_status,
        region: filters.advancedFilters?.region
      });

      if (result.success) {
        setSalesData(result.data);
        console.log('✅ Relatório de Vendas carregado:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Carrega relatório ao montar componente
   */
  useEffect(() => {
    loadSalesReport();
  }, [loadSalesReport]);

  /**
   * Handle mudança de período
   */
  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => ({
      ...prev,
      period: newPeriod
    }));
  };

  /**
   * Handle filtros avançados
   */
  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadSalesReportWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Handle exportar CSV
   */
  const handleExport = async () => {
    try {
      const blob = await ReportsService.exportCSV('sales', filters);
      ReportsService.downloadCSV(blob, `relatorio-vendas-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('❌ Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Esta funcionalidade será implementada em breve.');
    }
  };

  /**
   * Gráfico de vendas por status
   */
  const salesByStatusChart = salesData?.charts?.salesByStatus ? {
    data: {
      labels: salesData.charts.salesByStatus.map(item => {
        const labels = {
          paid: 'Pago',
          pending: 'Pendente',
          cancelled: 'Cancelado',
          refunded: 'Reembolsado'
        };
        return labels[item.status] || item.status;
      }),
      datasets: [
        {
          data: salesData.charts.salesByStatus.map(item => item.count),
          backgroundColor: [
            '#28a745',
            '#ffc107',
            '#dc3545',
            '#17a2b8'
          ],
          borderWidth: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  } : null;

  /**
   * Gráfico de vendas por método de pagamento
   */
  const salesByMethodChart = salesData?.charts?.salesByMethod ? {
    data: {
      labels: salesData.charts.salesByMethod.map(item => {
        const labels = {
          credit_card: 'Cartão de Crédito',
          pix: 'PIX',
          boleto: 'Boleto'
        };
        return labels[item.method] || item.method;
      }),
      datasets: [
        {
          label: 'Vendas',
          data: salesData.charts.salesByMethod.map(item => item.count),
          backgroundColor: [
            '#007bff',
            '#28a745',
            '#ffc107'
          ],
          borderWidth: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: chartColors
          }
        },
        x: {
          grid: {
            color: chartColors
          }
        }
      }
    }
  } : null;

  /**
   * Gráfico de vendas por produto
   */
  const salesByProductChart = salesData?.charts?.salesByProduct ? {
    data: {
      labels: salesData.charts.salesByProduct.map(item => item.product_name),
      datasets: [
        {
          label: 'Receita (R$)',
          data: salesData.charts.salesByProduct.map(item => item.revenue / 100),
          backgroundColor: '#007bff',
          borderWidth: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => `R$ ${context.parsed.x.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: chartColors
          },
          beginAtZero: true,
          ticks: {
            callback: (value) => `R$ ${value.toLocaleString('pt-BR')}`
          }
        },
        y: {
          grid: {
            color: chartColors
          }
        }
      }
    }
  } : null;

  /**
   * Colunas da tabela de vendas
   */
  const salesColumns = [
    { key: 'sale_id', label: 'ID', sortable: true },
    { key: 'date', label: 'Data', format: 'datetime', sortable: true },
    { key: 'product_name', label: 'Produto', sortable: true },
    { key: 'customer_name', label: 'Cliente', sortable: true },
    { key: 'affiliate_name', label: 'Afiliado', sortable: true },
    { 
      key: 'payment_method', 
      label: 'Método',
      sortable: true,
      render: (value) => {
        const labels = {
          credit_card: 'Cartão',
          pix: 'PIX',
          boleto: 'Boleto'
        };
        return labels[value] || value;
      }
    },
    { key: 'amount', label: 'Valor', format: 'currency', sortable: true },
    { key: 'total_commission', label: 'Comissão', format: 'currency', sortable: true },
    { 
      key: 'payment_status', 
      label: 'Status',
      sortable: true,
      render: (value) => {
        const colors = {
          paid: 'success',
          pending: 'warning',
          cancelled: 'danger',
          refunded: 'info'
        };
        const labels = {
          paid: 'Pago',
          pending: 'Pendente',
          cancelled: 'Cancelado',
          refunded: 'Reembolsado'
        };
        return <span className={`badge bg-${colors[value] || 'secondary'}`}>{labels[value] || value}</span>;
      }
    }
  ];

  return (
    <MasterLayout>
      <Container fluid style={{ padding: '32px 24px', minHeight: '100vh' }}>
      {/* Cabeçalho com Botões */}
      <div style={{
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e9ecef',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ flex: '1', minWidth: '300px' }}>
          <div style={{
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon icon="mdi:cart" style={{ color: '#007bff', fontSize: '22px' }} />
            <span className='text-contrast' style={{
              fontSize: '22px',
              fontWeight: '700',
              // color: '#212529'
            }}>
              Relatório de Vendas & Comissões
            </span>
          </div>
          <p style={{
            fontSize: '16px',
            color: '#6c757d',
            margin: 0
          }}>
            Análise detalhada de vendas, métodos de pagamento e comissões
          </p>
        </div>
        
        {/* Botões de Ação */}
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Botão Filtros Avançados */}
          <Button
            variant={Object.keys(filters.advancedFilters || {}).filter(k => k !== 'user_type' && k !== 'user_id' && filters.advancedFilters[k]).length > 0 ? 'primary' : 'outline-primary'}
            onClick={() => setShowFilterDrawer(true)}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:filter" style={{ fontSize: '18px' }} />
            Filtros Avançados
          </Button>

          {/* Botão Exportar */}
          <Button
            variant="outline-success"
            onClick={handleExport}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:file-excel" style={{ fontSize: '18px' }} />
            Exportar CSV
          </Button>

          {/* Botão Atualizar */}
          <Button
            variant="primary"
            onClick={loadSalesReport}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon 
              icon="mdi:refresh" 
              style={{ fontSize: '18px' }}
              className={loading ? 'spin' : ''}
            />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <FilterBar
        period={filters.period}
        onPeriodChange={handlePeriodChange}
        onApplyAdvancedFilters={handleAdvancedFilters}
        onRefresh={loadSalesReport}
        onExport={handleExport}
        loading={loading}
        advancedFilters={filters.advancedFilters}
        onUserFilterChange={handleUserFilterChange}
        showExport={false}
      />

      {/* Drawer de Filtros Avançados */}
      <FilterDrawer
        show={showFilterDrawer}
        onHide={() => setShowFilterDrawer(false)}
        onApply={(newFilters) => {
          setShowFilterDrawer(false);
          handleAdvancedFilters(newFilters);
        }}
        currentFilters={filters.advancedFilters}
      />

      {/* Erro */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
          {error}
        </Alert>
      )}

      {/* KPIs */}
      <Row className="g-4 mb-5">
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Total de Vendas"
            value={loading ? '...' : (salesData?.kpis?.total_sales || 0).toLocaleString('pt-BR')}
            subtitle={loading ? '' : `${salesData?.kpis?.unique_customers || 0} clientes únicos`}
            icon="mdi:cart"
            variant="blue"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Receita Paga"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(salesData?.kpis?.paid_revenue || 0)}
            subtitle={loading ? '' : `${salesData?.kpis?.conversion_rate?.toFixed(1) || 0}% taxa de conversão`}
            icon="mdi:cash"
            variant="green"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Ticket Médio"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(salesData?.kpis?.avg_ticket || 0)}
            subtitle={loading ? '' : `${salesData?.kpis?.total_sales || 0} vendas`}
            icon="mdi:chart-line"
            variant="orange"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Comissões Totais"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(salesData?.kpis?.total_commissions || 0)}
            subtitle={loading ? '' : 'Todas as comissões pagas'}
            icon="mdi:account-cash"
            variant="purple"
          />
        </Col>
      </Row>

      {/* Gráficos */}
      <Row className="g-4 mb-5">
        {/* Vendas por Status */}
        <Col xs={12} lg={4}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <span className='text-constrast' style={{
                fontSize: '18px',
                fontWeight: '600',
                // color: '#212529',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:chart-donut" style={{ color: '#007bff' }} />
                Status das Vendas
              </span>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : salesByStatusChart ? (
                <div style={{ height: '300px' }}>
                  <Doughnut data={salesByStatusChart.data} options={salesByStatusChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Vendas por Método */}
        <Col xs={12} lg={4}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <span className='text-contrast' style={{
                fontSize: '18px',
                fontWeight: '600',
                // color: '#212529',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:credit-card" style={{ color: '#28a745' }} />
                Métodos de Pagamento
              </span>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : salesByMethodChart ? (
                <div style={{ height: '300px' }}>
                  <Bar data={salesByMethodChart.data} options={salesByMethodChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Vendas por Produto */}
        <Col xs={12} lg={4}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <span className='text-contrast' style={{
                fontSize: '18px',
                fontWeight: '600',
                // color: '#212529',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:package-variant" style={{ color: '#ffc107' }} />
                Top 10 Produtos
              </span>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : salesByProductChart ? (
                <div style={{ height: '300px', overflowY: 'auto' }}>
                  <Bar data={salesByProductChart.data} options={salesByProductChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Vendas */}
      <Row>
        <Col xs={12}>
          <span className='text-contrast' style={{
            fontSize: '18px',
            fontWeight: '600',
            // color: '#212529',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon icon="mdi:format-list-bulleted" style={{ color: '#6c757d' }} />
            Lista de Vendas (últimas 100)
          </span>
          <SmartTable
            data={salesData?.salesList || []}
            columns={salesColumns}
            loading={loading}
            searchable={true}
            itemsPerPage={20}
            emptyMessage="Nenhuma venda encontrada"
          />
        </Col>
      </Row>
      </Container>
    </MasterLayout>
  );
};

export default SalesReport;

